import { useCallback, useEffect, useState } from 'react';
import useCustomEntities from './useEntities';

export type entraIdProps = {
  area_uid: string;
  domain_uid: string;
  entraid_contributor_objectid: string;
  entraid_team_objectid: string;
  name: string;
  sub_type: string;
  type: string;
  uid: string;
};

export interface UseContributingProductsParams {
  productData?: { response?: any[] };
  entraIdPeopleAppList: entraIdProps[];
  hierarchyList: any[];
  contributingType?: 'with ProductData' | 'without ProductData';
  teamName?: string;
}

export function useContributingProducts({
  productData,
  entraIdPeopleAppList,
  hierarchyList,
  contributingType,
  teamName,
}: UseContributingProductsParams) {
  const [contributingProductsTeam, setContributingProductsTeam] = useState<
    any[]
  >([]);

  const { getCustomEntitiesByKind } = useCustomEntities('System');

  const fetchContributingProducts = useCallback(async () => {
    if (!productData?.response?.length) return;

    const entraIdList = productData.response
      .filter(
        (item, index, self) =>
          index ===
          self.findIndex(
            t =>
              t.teamname?.toLowerCase()?.trim() ===
              item.teamname?.toLowerCase()?.trim(),
          ),
      )
      .map(item =>
        entraIdPeopleAppList?.find(
          (x: entraIdProps) =>
            x?.entraid_team_objectid?.toLowerCase()?.trim() ===
            item?.teamobjectid?.toLowerCase()?.trim(),
        ),
      )
      .filter(Boolean);

    const entities = (await getCustomEntitiesByKind()).items;
    const matchingProductList: any[] = [];

    if (entities.length > 0 && entraIdList.length > 0) {
      for (const entity of entities) {
        const contributingTeams =
          (entity as any)?.spec?.contributingTeams || [];

        for (const team of contributingTeams) {
          const matchedEntra = entraIdList.find(
            (entra: any) =>
              entra?.uid?.trim().replace(/\s+/g, ' ').toLowerCase() ===
              team?.toLowerCase().trim().replace(/\s+/g, ' '),
          ) as unknown as entraIdProps;

          if (matchedEntra) {
            matchingProductList.push({
              entity,
              teamData: hierarchyList?.find(
                (x: { teamname: string }) =>
                  x?.teamname?.toLowerCase() ===
                  matchedEntra?.name?.toLowerCase(),
              ),
            });
          }
        }
      }
    }

    const uniqueList = matchingProductList.filter(
      (item_, index, self) =>
        self.findIndex(
          item =>
            item.entity?.metadata?.title === item_?.entity?.metadata?.title,
        ) === index,
    );

    setContributingProductsTeam(uniqueList);
  }, [productData, entraIdPeopleAppList, hierarchyList]);
  const fetchContributingProductsByTeam = useCallback(
    async (teamName?: string) => {
      if (!teamName) {
        setContributingProductsTeam([]);
        return;
      }

      const matchedEntra = entraIdPeopleAppList.find(
        (entra: entraIdProps) =>
          entra?.entraid_team_objectid
            ?.trim()
            .replace(/\s+/g, ' ')
            .toLowerCase() ===
          teamName?.toLowerCase().trim().replace(/\s+/g, ' '),
      );

      if (!matchedEntra) {
        setContributingProductsTeam([]);
        return;
      }

      const entities = (await getCustomEntitiesByKind()).items;
      const contributingProducts = entities.filter(entity => {
        const teams = entity.spec?.contributingTeams as string[] | undefined;
        return teams?.includes(matchedEntra.uid);
      });

      setContributingProductsTeam(prev => {
        const prevStr = JSON.stringify(prev);
        const newStr = JSON.stringify(contributingProducts);
        return prevStr === newStr ? prev : contributingProducts;
      });
    },
    [entraIdPeopleAppList, getCustomEntitiesByKind],
  );

  useEffect(() => {
    if (
      productData &&
      productData?.response?.length!! > 0 &&
      contributingType === 'with ProductData' &&
      !teamName
    ) {
      fetchContributingProducts();
    } else if (teamName) {
      fetchContributingProductsByTeam(teamName);
    } else {
      setContributingProductsTeam([]);
    }
  }, [
    fetchContributingProducts,
    fetchContributingProductsByTeam,
    productData,
    teamName,
    contributingType,
  ]);

  return { contributingProductsTeam };
}
